import React, { lazy, useEffect, useLayoutEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import dompurify from 'dompurify';

import { useAuth } from '@sine/auth-client';
import { useTeamSwitcher } from '@sine/sine-team-switcher';
import { Loader, useNotification } from '@sine/sine-ui-components';

import ApplicationFrame from './ApplicationFrame';
import oauthRectifierService from './authentication/oauthRectifier.service';
import LinkProvider from './routing/LinkProvider';
import { BASE_PATH, PERMISSIONS, ROUTE_ID, ROUTE_PATH } from './routing/routeConfig';
import { useFeatures } from './shared/customHooks/useFeatures';
import WithPermissionsCheck from './shared/hoc/withPermissionsCheck';
import wrapLazyLoad from './shared/hoc/wrapLazyLoad';
import { AclAction } from './team/permission/constants/actions';

const PermissionStatus = wrapLazyLoad(lazy(() => import('./team/permission/PermissionStatus')));
const ActivityPage = wrapLazyLoad(lazy(() => import('./activity/components/ActivityPage')));
const FeatureNotEnabledPage = wrapLazyLoad(lazy(() => import('./FeatureNotEnabled/FeatureNotEnabledPage')));
const ClaimAccountPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/ClaimAccountPage')));
const ClaimHostPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/ClaimHostPage')));
const ClaimSupplierProfilePage = wrapLazyLoad(lazy(() => import('./supplier/ClaimSupplierProfilePage')));
const ConfirmAppointees = wrapLazyLoad(lazy(() => import('./pages/MyProfile/components/Appointees/ConfirmAppointees')));
const ConnectionConfirmPage = wrapLazyLoad(lazy(() => import('./user/connection/components/ConnectionConfirmPage')));
const DisplaysPage = wrapLazyLoad(lazy(() => import('./display/pages/DisplaysPage')));
const FileDownload = wrapLazyLoad(lazy(() => import('./report/file-download/components/FileDownload')));
const ForgotPasswordPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/ForgotPasswordPage')));
const HidCredentialsFileDownloadPage = wrapLazyLoad(
  lazy(
    () =>
      import('./site/integrations/instances/genericHidCredentialDownload/components/HidCredentialsFileDownloadPage'),
  ),
);
const HostsPage = wrapLazyLoad(lazy(() => import('./host')));
const IcsInviteConfirmPage = wrapLazyLoad(lazy(() => import('./pages/IcsInvite/IcsInviteConfirmPage')));
const IcsInviteConfirmUpdatePage = wrapLazyLoad(lazy(() => import('./pages/IcsInvite/IcsInviteConfirmUpdatePage')));
const InvitationsFeatureToggle = wrapLazyLoad(lazy(() => import('./pages/Invitations/InvitationsPage')));
const LocationsPage = wrapLazyLoad(lazy(() => import('./locations/LocationsPage')));
const LoginPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/LoginPage')));
const AssetsPage = wrapLazyLoad(lazy(() => import('./assets/AssetsPage')));
const LoginSaml = wrapLazyLoad(lazy(() => import('./pre-login/LoginSaml')));
const LoginSamlError = wrapLazyLoad(lazy(() => import('./pre-login/LoginSamlError')));
const MyPassesPage = wrapLazyLoad(lazy(() => import('./my-passes/pages/MyPassesPage')));
const PassStatusPage = wrapLazyLoad(lazy(() => import('./pass/components/PassStatusPage')));
const MyProfilePage = wrapLazyLoad(lazy(() => import('./pages/MyProfile/MyProfilePage')));
const ReportsPage = wrapLazyLoad(lazy(() => import('./report/components/reportsPage')));
const ResetPasswordPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/ResetPasswordPage')));
const SamlVerifyEmailPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/SamlVerifyEmailPage')));
const SetupOrganizationPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/SignUpOrganizationPage')));
const SignUpSitePage = wrapLazyLoad(lazy(() => import('./pre-login/pages/SignUpSitePage')));
const SetupSupplierTeamPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/SetupSupplierTeamPage')));
const SignUpUserPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/SignUpUserPage')));

const UpdateCredentialRequestStatusPage = wrapLazyLoad(
  lazy(() => import('./workplace/components/UpdateCredentialRequestStatusPage')),
);
const VerifyEmailPage = wrapLazyLoad(lazy(() => import('./pre-login/pages/VerifyEmail')));
const WalletPassDownloadPage = wrapLazyLoad(lazy(() => import('./pages/Wallet/WalletPassDownloadPage')));
const ZonesPage = wrapLazyLoad(lazy(() => import('./pages/Zones/ZonesPage')));
const WatchlistsPage = wrapLazyLoad(lazy(() => import('./watchlists/WatchlistsPage')));

const DisplayErrorNotificationWithRedirect = () => {
  const { addNotification } = useNotification();

  useEffect(() => {
    addNotification({
      heading: 'Could not accept permissions',
      message: 'Please log out before attempting to set your password.',
      variant: 'danger',
    });
  }, [addNotification]);

  return <Redirect to={{ pathname: BASE_PATH }} />;
};

export default function AppFrameWrapper() {
  const { currentTeam } = useTeamSwitcher();
  const { authState, isImpersonating: isSysAdmin, userDetails } = useAuth();
  const { isLoading: featuresLoading, features } = useFeatures();

  const { id: currentTeamId } = currentTeam || {};
  const history = useHistory();

  let isCoreEnabled = featuresLoading || features.includes('CORE');
  let isZonesEnabled = featuresLoading || features.includes('ZONES');

  // A system admin should always be able to impersonate, even if the account is not verified.
  const isUserEmailVerified = isSysAdmin || userDetails?.verify.email.completed;

  // For a basic user core
  if (!currentTeamId) {
    isCoreEnabled = true;
    isZonesEnabled = false;
  }

  const SignUpSitePageWithPermission = WithPermissionsCheck(SignUpSitePage, AclAction.TEAM_ADMIN, BASE_PATH);
  const ActivityPageWithPermission = WithPermissionsCheck(ActivityPage, PERMISSIONS.PASS, ROUTE_PATH.USER_SETTINGS);
  const HostsPageWithPermission = WithPermissionsCheck(HostsPage, PERMISSIONS.HOST, BASE_PATH);
  const ReportingPageWithPermission = WithPermissionsCheck(ReportsPage, PERMISSIONS.REPORTING, BASE_PATH);
  const AssetPageWithPermission = WithPermissionsCheck(AssetsPage, PERMISSIONS.ASSET, BASE_PATH);
  const LocationsPageWithPermission = WithPermissionsCheck(LocationsPage, PERMISSIONS.SITE, BASE_PATH);
  const WatchlistsPageWithPermission = WithPermissionsCheck(WatchlistsPage, AclAction.TEAM_ADMIN, BASE_PATH);
  const ZonesPageWithPermission = WithPermissionsCheck(ZonesPage, AclAction.SITE_UPDATE, BASE_PATH);
  const DisplaysPageWithPermission = WithPermissionsCheck(DisplaysPage, PERMISSIONS.DISPLAY, BASE_PATH);
  const InvitationsFeatureToggleWithPermission = WithPermissionsCheck(
    InvitationsFeatureToggle,
    PERMISSIONS.INVITATION,
    BASE_PATH,
  );

  // Redirect for integration authentication.
  useLayoutEffect(() => {
    oauthRectifierService.rectify();
  });

  useEffect(() => {
    /* we include query parameters in links sent to hosts that have an existing account so they
    can be shown relevant information on the login page. If the user is already logged in, the link takes them straight
    to the activity feed, so we need to discard the query string.
    */
    if (authState === 'loggedIn' && window.location.pathname === BASE_PATH && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      if ((params.get('teamId') && params.get('type') === 'host') || params.get('account-type')) {
        history.replace(location.pathname);
      }
    }
  }, [authState, history]);

  return (
    <Switch>
      <Route path={ROUTE_PATH.APPOINTEE_CONFIRM}>
        <ConfirmAppointees />
      </Route>

      <Route path={ROUTE_PATH.ICS_CONFIRM}>
        <IcsInviteConfirmPage />
      </Route>

      <Route path={ROUTE_PATH.WALLET_DOWNLOAD_WALLET_PASS}>
        <WalletPassDownloadPage />
      </Route>

      <Route path={ROUTE_PATH.ICS_CONFIRM_UPDATE}>
        <IcsInviteConfirmUpdatePage />
      </Route>

      <Route path={ROUTE_PATH.PERMISSION_STATUS}>
        <PermissionStatus />
      </Route>

      {authState === 'awaitingLoginChallenge' ? (
        <>
          <Route path='/challenge'>
            <>{/* show nothing, used as a control for challenge modal */}</>
          </Route>
          <Route exact path={ROUTE_PATH.LOGIN}>
            <Redirect to='/challenge' />
          </Route>
          <Route exact path={BASE_PATH}>
            <Redirect to='/challenge' />
          </Route>
        </>
      ) : authState === 'loading' ? (
        <Loader show fillAndCenter />
      ) : authState === 'loggedIn' ? (
        <LinkProvider>
          <ApplicationFrame isCoreEnabled={isCoreEnabled} isSysAdmin={isSysAdmin}>
            {isUserEmailVerified ? (
              <>
                {isCoreEnabled || isSysAdmin ? (
                  <Switch>
                    <Route exact path={BASE_PATH}>
                      <ActivityPageWithPermission />
                    </Route>
                    <Route exact path={ROUTE_PATH.INVITATIONS}>
                      <InvitationsFeatureToggleWithPermission />
                    </Route>
                    <Route exact path={ROUTE_PATH.REPORTS}>
                      <ReportingPageWithPermission />
                    </Route>
                    <Route path={ROUTE_PATH.ASSETS}>
                      <AssetPageWithPermission />
                    </Route>
                    <Route path={ROUTE_PATH.FILE_DOWNLOAD}>
                      <FileDownload />
                    </Route>
                    <Route exact path={ROUTE_PATH.MY_PASSES}>
                      <MyPassesPage />
                    </Route>
                    <Route path={ROUTE_PATH.LOCATIONS}>
                      <LocationsPageWithPermission />
                    </Route>
                    <Route exact path={ROUTE_PATH.ZONES}>
                      {isZonesEnabled ? <ZonesPageWithPermission /> : <Redirect to={{ pathname: BASE_PATH }} />}
                    </Route>
                    <Route exact path={[ROUTE_PATH.WATCHLISTS, ROUTE_PATH.TEAM_CHECKLISTS, ROUTE_PATH.TEAM_WATCHLISTS]}>
                      <WatchlistsPageWithPermission />
                    </Route>
                    <Route exact path={ROUTE_PATH.HOSTS}>
                      <HostsPageWithPermission />
                    </Route>
                    <Route path={ROUTE_PATH.DISPLAYS}>
                      <DisplaysPageWithPermission />
                    </Route>
                    <Route
                      path={ROUTE_PATH.TEAM}
                      component={() => {
                        window.location.replace(window.env.REACT_APP_SINE_ADMIN_CLIENT);
                        return null;
                      }}
                    />
                    <Route exact path={ROUTE_PATH.USER_SETTINGS}>
                      <MyProfilePage />
                    </Route>
                    <Route path={ROUTE_PATH.HID_CREDENTIALS_DOWNLOAD} exact>
                      <HidCredentialsFileDownloadPage />
                    </Route>
                    <Route path={ROUTE_PATH.OUTLOOK_CONFIRM}>
                      <ConnectionConfirmPage />
                    </Route>
                    <Route
                      path={ROUTE_PATH.SETUP_SITE}
                      render={() => (
                        <Redirect
                          to={{
                            pathname: ROUTE_PATH.SETUP_ORGANIZATION,
                          }}
                        />
                      )}
                    />
                    <Route path={ROUTE_PATH.SETUP_ORGANIZATION} component={SetupOrganizationPage} />
                    <Route path={ROUTE_PATH.SIGNUP_SITE} component={SignUpSitePageWithPermission} />
                    <Route path={ROUTE_PATH.SETUP_TEAM}>
                      <SetupSupplierTeamPage />
                    </Route>
                    <Route path={ROUTE_PATH.UPDATE_CREDENTIAL} component={UpdateCredentialRequestStatusPage} />
                    <Route path={ROUTE_PATH.PASS_STATUS}>
                      <PassStatusPage />
                    </Route>
                    <Route path={ROUTE_PATH.SUPPLIER_PROFILE_CLAIM} exact>
                      <ClaimSupplierProfilePage />
                    </Route>
                    <Route path={ROUTE_PATH.SIGNUP_CLAIM_ACCOUNT}>
                      <DisplayErrorNotificationWithRedirect />
                    </Route>
                    <Route path={ROUTE_PATH.SIGNUP_CLAIM_HOST}>
                      <DisplayErrorNotificationWithRedirect />
                    </Route>
                    <Route path={ROUTE_PATH.ACCOUNT_VERIFY}>
                      <VerifyEmailPage />
                    </Route>

                    <Route path='*' render={() => <Redirect to={{ pathname: BASE_PATH }} />} />
                  </Switch>
                ) : (
                  <Switch>
                    <Route exact path={ROUTE_PATH.MY_PASSES}>
                      <MyPassesPage />
                    </Route>
                    <Route exact path={ROUTE_PATH.USER_SETTINGS}>
                      <MyProfilePage />
                    </Route>
                    <Route path={ROUTE_PATH.SETUP_TEAM}>
                      <SetupSupplierTeamPage />
                    </Route>
                    <Route path={ROUTE_PATH.SUPPLIER_PROFILE_CLAIM} exact>
                      <ClaimSupplierProfilePage />
                    </Route>
                    <Route path='*' component={FeatureNotEnabledPage} />
                  </Switch>
                )}
              </>
            ) : (
              <Switch>
                <Route path={ROUTE_PATH.ACCOUNT_VERIFY}>
                  <VerifyEmailPage />
                </Route>
                <Route
                  path='*'
                  render={() => (
                    <Redirect to={{ pathname: ROUTE_PATH.ACCOUNT_VERIFY, search: window.location.search }} />
                  )}
                />
              </Switch>
            )}
          </ApplicationFrame>
        </LinkProvider>
      ) : (
        <Switch>
          <Route
            path={ROUTE_PATH.LOGIN}
            key={ROUTE_ID.LOGIN}
            exact
            render={(routeProps) => <LoginPage {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.SIGN_UP_USER}
            key={ROUTE_ID.SIGN_UP_USER}
            render={(routeProps) => <SignUpUserPage {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.SIGN_UP}
            key={ROUTE_ID.SIGN_UP}
            exact
            render={(routeProps) => (
              <Redirect
                to={{
                  pathname: ROUTE_PATH.SIGN_UP_USER,
                  search: dompurify.sanitize(routeProps.location.search),
                  state: routeProps.location,
                }}
              />
            )}
          />
          <Route
            path={ROUTE_PATH.SIGN_UP_BASIC}
            key={ROUTE_ID.SIGN_UP_BASIC}
            render={(routeProps) => (
              <Redirect
                to={{
                  pathname: ROUTE_PATH.SIGN_UP_USER,
                  search: routeProps.location.search
                    ? dompurify.sanitize(routeProps.location.search + '&account-type=basic')
                    : 'account-type=basic',
                  state: routeProps.location,
                }}
              />
            )}
          />
          <Route
            path={ROUTE_PATH.SIGN_UP_FULL}
            key={ROUTE_ID.SIGN_UP_FULL}
            render={(routeProps) => (
              <Redirect
                to={{
                  pathname: ROUTE_PATH.SIGN_UP_USER,
                  search: routeProps.location.search
                    ? dompurify.sanitize(routeProps.location.search + '&account-type=full')
                    : 'account-type=full',
                  state: routeProps.location,
                }}
              />
            )}
          />
          <Route
            path={ROUTE_PATH.FORGOT_PASSWORD}
            key={ROUTE_ID.FORGOT_PASSWORD}
            render={(routeProps) => <ForgotPasswordPage {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.RESET_PASSWORD}
            key={ROUTE_ID.RESET_PASSWORD}
            render={(routeProps) => <ResetPasswordPage {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.SIGNUP_CLAIM_ACCOUNT}
            key={ROUTE_ID.SIGNUP_CLAIM_ACCOUNT}
            render={(routeProps) => <ClaimAccountPage {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.SIGNUP_CLAIM_HOST}
            key={ROUTE_ID.SIGNUP_CLAIM_HOST}
            render={(routeProps) => <ClaimHostPage {...(routeProps as any)} />}
          />
          <Route
            exact
            path={ROUTE_PATH.LOGIN_SAML}
            key={ROUTE_ID.LOGIN_SAML}
            render={(routeProps) => <LoginSaml {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.LOGIN_SAML_ERROR}
            key={ROUTE_ID.LOGIN_SAML_ERROR}
            render={(routeProps) => <LoginSamlError {...(routeProps as any)} />}
          />
          <Route
            path={ROUTE_PATH.SAML_VERIFY_EMAIL}
            key={ROUTE_ID.SAML_VERIFY_EMAIL}
            render={(routeProps) => <SamlVerifyEmailPage {...(routeProps as any)} />}
          />
          <Route
            path='*'
            render={(routeProps) => (
              <Redirect
                to={{
                  pathname: ROUTE_PATH.LOGIN,
                  search: dompurify.sanitize(routeProps.location.search),
                  state: routeProps.location,
                }}
              />
            )}
          />
        </Switch>
      )}
    </Switch>
  );
}
